import currencies from '../currencies.json';

/**
 * Formats a number as a price with commas and decimal places
 * @param {number|string} price - The price to format
 * @param {number} [decimals=2] - Number of decimal places to show
 * @returns {string} Formatted price string
 * @example
 * formatPrice(22649.99) // returns "22,649.99"
 * formatPrice(1000) // returns "1,000.00"
 * formatPrice(1000.5) // returns "1,000.50"
 */
export const formatPrice = (price, decimals = 2) => {
  // Convert to number if string
  const numPrice = typeof price === 'string' ? parseFloat(price) : price;
  
  // Check if the price is a valid number
  if (isNaN(numPrice)) {
    return '0.00';
  }

  // Format the number with commas and fixed decimal places
  return numPrice.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

/**
 * Formats a date from YYYY/MM/DD to DD-MM-YYYY
 * @param {string} dateString - The date string in YYYY/MM/DD format
 * @returns {string} Formatted date string in DD-MM-YYYY format
 * @example
 * formatDate("2024/03/15") // returns "15-03-2024"
 * formatDate("2024/12/01") // returns "01-12-2024"
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';

  // Split the date string and create a Date object
  const [year, month, day] = dateString.split('/');
  const date = new Date(year, month - 1, day);

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return '';
  }

  // Format the date as DD-MM-YYYY
  const formattedDay = String(date.getDate()).padStart(2, '0');
  const formattedMonth = String(date.getMonth() + 1).padStart(2, '0');
  const formattedYear = date.getFullYear();

  return `${formattedDay}-${formattedMonth}-${formattedYear}`;
};

/**
 * Formats time from 24-hour format (HH:mm) to 12-hour format with AM/PM
 * @param {string} timeString - The time string in HH:mm format
 * @returns {string} Formatted time string in hh:mm AM/PM format
 * @example
 * formatTime("13:00") // returns "01:00 PM"
 * formatTime("09:30") // returns "09:30 AM"
 * formatTime("00:00") // returns "12:00 AM"
 */
export const formatTime = (timeString) => {
  if (!timeString) return '';

  // Split the time string into hours and minutes
  const [hours, minutes] = timeString.split(':').map(Number);

  // Check if the time is valid
  if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return '';
  }

  // Create a date object with the time
  const date = new Date();
  date.setHours(hours, minutes);

  // Format the time using toLocaleTimeString
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Gets the currency code from the currency name
 * @param {string} currencyName - The name of the currency (e.g., "US Dollar", "Euro")
 * @returns {string} The currency code (e.g., "USD", "EUR")
 * @example
 * getCurrencyCode("US Dollar") // returns "USD"
 * getCurrencyCode("Euro") // returns "EUR"
 */
export const getCurrencyCode = (currencyName) => {
  if (!currencyName) return '';
  
  const currency = currencies.data.find(
    curr => curr.name.toLowerCase() === currencyName.toLowerCase()
  );
  
  return currency ? currency.code : '';
}; 

